<script lang="ts">
  import { DocumentsStore, DragType, isDragging, isDraggingAtTheRoot } from "$stores/documents";
  import { canChangeSpaceGroup } from "app/stores/permissions";
  import { dndzone, TRIGGERS } from "svelte-dnd-action";
  import { flip } from "svelte/animate";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import { communitySlug } from "$stores/community";
  import KBNavItem from "./KBNavItem.svelte";
  import {
    handleSharedDraggingBehaviour,
    isDndPlaceholder,
    transformDraggedElement,
  } from "$components/knowledge-base/KnowledgeBaseNavigation/dndUtils";
  import DndPlaceholder from "$components/knowledge-base/KnowledgeBaseNavigation/DndPlaceholder.svelte";
  import { isNestedDocument } from "models/documents";
  import KBFolder from "$components/knowledge-base/KnowledgeBaseNavigation/KBFolder.svelte";

  export let docs: DocumentsStore;

  const FLIP_DURATION_MS = 200;
  const isDesktop = getMediaQuery("desktop");
  const { folders } = docs;

  $: [defaultFolder] = $folders;
  const { currentDragType } = docs;

  let hasDraggedEntered: boolean;

  // we reset the hasDraggedEntered whenever the isDragging value changes
  $: {
    hasDraggedEntered = false;
    $isDragging;
  }

  async function sortDocs(e: CustomEvent) {
    docs.preventUpdates(); // prevent server updates from colliding with user

    handleSharedDraggingBehaviour(e);

    // if the drags and item inside the section
    // and we set the hasDraggedEntered to true
    if (e.detail.info.trigger === TRIGGERS.DRAGGED_ENTERED) {
      hasDraggedEntered = true;
    }

    // if the user drags an item outside of the section, we set the drag value at the root state to true
    if (e.detail.info.trigger === TRIGGERS.DRAGGED_LEFT) {
      $isDraggingAtTheRoot = true;
    }

    if (e.detail.info.trigger === TRIGGERS.DRAG_STARTED) {
      const destination = $defaultFolder.items.findIndex(
        (space) => space.uuid === e.detail.info.id
      );
      const folderItem = $defaultFolder.items[destination];
      // can be either "nested_folder" or "nested_document"
      $currentDragType =
        folderItem.folderItemType === "nested_folder"
          ? DragType.NestedFolder
          : DragType.NestedDocument;
    }

    // user has moved folder update UI
    $defaultFolder.items = e.detail.items;
    // folder has been dropped into a zone, update the order in the backend
    if (e.detail.info.trigger === TRIGGERS.DROPPED_INTO_ZONE) {
      const destination = $defaultFolder.items.findIndex(
        (space) => space.uuid === e.detail.info.id
      );
      const folderItem = $defaultFolder.items[destination];
      folderItem.moveAfterDragAndDrop(destination, $defaultFolder);
    }
    //dropped outside of any zone, we don't need to update the order
  }
</script>

{#if $defaultFolder}
  <!-- Default docs -->
  <div
    class="rounded pr-1 pb-0"
    use:dndzone={{
      items: $defaultFolder.items,
      flipDurationMs: FLIP_DURATION_MS,
      dragDisabled: !$canChangeSpaceGroup || !$isDesktop,
      type: "docs",
      dropTargetClasses: [
        "min-h-[16px]",
        "overflow-hidden",
        "border",
        "border-dashed",
        "border-blue-400/75",
        "dark:border-blue-400/45",
        "transition-[padding]",
      ],
      dropTargetStyle: {
        marginBottom: $defaultFolder.items.length > 0 ? "0px" : "-16px",
        paddingBottom: hasDraggedEntered ? "8px" : "0",
      },
      // The default folder is technically a Section, it accepts Categories and Docs
      dropFromOthersDisabled: $currentDragType === DragType.Section,
      transformDraggedElement,
    }}
    on:consider={sortDocs}
    on:finalize={sortDocs}
    class:mt-2={$defaultFolder.items.length > 0}
  >
    {#if $defaultFolder.items.length === 0}
      <!--
      Important, when these nav dropzones are completely empty, there is a bug where dropzones don't open properly. It is
      not enough to set min height in dropTargetClasses.
      -->
      <div />
    {/if}

    {#each $defaultFolder.items as folderItem (folderItem.uuid)}
      <div animate:flip={{ duration: FLIP_DURATION_MS }}>
        {#if isDndPlaceholder(folderItem)}
          <DndPlaceholder />
        {:else if isNestedDocument(folderItem)}
          <div>
            <KBNavItem communitySlug={$communitySlug} document={folderItem} />
          </div>
        {:else}
          <KBFolder
            {docs}
            folder={folderItem}
            parentSection={defaultFolder}
            hasDraggedEnteredParent={hasDraggedEntered}
          />
        {/if}
      </div>
    {/each}
  </div>
{/if}
